import { useState, useEffect } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth.context';
import { useToast } from './use-toast';

export interface PlayerListItem {
  playerId: string;
  index: number;
  createdAt: string;
}

export interface PlayerList {
  id: string;
  name: string;
  userId: string | null;
  clubId: string | null;
  items: PlayerListItem[];
  createdAt: string;
  updatedAt: string;
}

export interface PlayerListsResponse {
  lists: PlayerList[];
}

export const usePlayerLists = () => {
  const api = useApi();
  const { user } = useAuth();
  const { toast } = useToast();
  const [lists, setLists] = useState<PlayerList[]>([]);
  const [defaultWishlist, setDefaultWishlist] = useState<PlayerList | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLists = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.time('api.get-lists');
      // Make sure to include the /api prefix for Quarkus API calls
      const endpoint = `/api/v1/users/${user.id}/lists`;
      console.log('Fetching player lists from:', endpoint);
      
      const response = await api.get<PlayerListsResponse>(endpoint);
      console.timeEnd('api.get-lists');
      
      console.log('Lists response:', response);
      setLists(response.lists);
      
      // Find the default wishlist
      const wishlist = response.lists.find(list => list.name === 'Wishlist');
      if (wishlist) {
        console.log('Default wishlist found:', wishlist);
        setDefaultWishlist(wishlist);
      } else {
        console.warn('No default wishlist found in response');
      }
    } catch (err) {
      console.error('Error fetching player lists:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch player lists');
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultWishlist = async () => {
    if (!user?.id) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Make sure to include the /api prefix for Quarkus API calls
      const endpoint = `/api/v1/users/${user.id}/lists/default-wishlist`;
      console.log('Fetching default wishlist from:', endpoint);
      
      const wishlist = await api.get<PlayerList>(endpoint);
      setDefaultWishlist(wishlist);
      return wishlist;
    } catch (err) {
      console.error('Error fetching default wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch default wishlist');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const addPlayerToWishlist = async (playerId: string) => {
    if (!user?.id) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Ensure we have the default wishlist
      let wishlist = defaultWishlist;
      if (!wishlist) {
        wishlist = await getDefaultWishlist();
        if (!wishlist) throw new Error('Failed to get default wishlist');
      }
      
      // Make sure to include the /api prefix for Quarkus API calls
      const endpoint = `/api/v1/users/${user.id}/lists/${wishlist.id}`;
      console.log('Making wishlist API call to:', endpoint);
      
      const updatedWishlist = await api.post<PlayerList>(
        endpoint,
        { playerId }
      );
      
      // Update the lists state
      setLists(prevLists => 
        prevLists.map(list => 
          list.id === updatedWishlist.id ? updatedWishlist : list
        )
      );
      
      setDefaultWishlist(updatedWishlist);
      return updatedWishlist;
    } catch (err) {
      console.error('Error adding player to wishlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to add player to wishlist');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const reorderWishlistItem = async (currentIndex: number, newIndex: number) => {
    if (!user?.id || !defaultWishlist) return null;
    
    // If indices are the same, no need to reorder
    if (currentIndex === newIndex) return defaultWishlist;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Store the original wishlist for rollback if needed
      const originalWishlist = { ...defaultWishlist, items: [...defaultWishlist.items] };
      
      // Get sorted items to work with
      const sortedItems = [...defaultWishlist.items].sort((a, b) => a.index - b.index);
      const itemToMove = sortedItems.find(item => item.index === currentIndex);
      
      if (!itemToMove) {
        throw new Error(`Item at index ${currentIndex} not found`);
      }
      
      // Create a deep copy of the item to move
      const movedItem = { ...itemToMove };
      
      // Create a new array with the item moved to the new position
      // This simulates what the backend will do
      const reorderedItems = sortedItems.filter(item => item.index !== currentIndex);
      
      // Insert the moved item at the appropriate position
      if (newIndex >= reorderedItems.length) {
        // If inserting at the end
        reorderedItems.push(movedItem);
      } else {
        reorderedItems.splice(newIndex, 0, movedItem);
      }
      
      // Update indices to be sequential
      const finalItems = reorderedItems.map((item, idx) => ({
        ...item,
        index: idx
      }));
      
      // Create an optimistic update for the UI
      const optimisticWishlist = {
        ...defaultWishlist,
        items: finalItems
      };
      
      // Update the UI optimistically
      setDefaultWishlist(optimisticWishlist);
      
      // Call the API to persist the change
      // Make sure to include the /api prefix for Quarkus API calls
      const endpoint = `/api/v1/users/${user.id}/lists/${defaultWishlist.id}`;
      console.log('Reordering wishlist item at:', endpoint);
      
      const updatedWishlist = await api.patch<PlayerList>(
        endpoint,
        {
          operation: 'reorder_item',
          currentIndex,
          newIndex
        }
      );
      
      // Update the lists state with the server response
      setLists(prevLists => 
        prevLists.map(list => 
          list.id === updatedWishlist.id ? updatedWishlist : list
        )
      );
      
      setDefaultWishlist(updatedWishlist);
      return updatedWishlist;
    } catch (err) {
      console.error('Error reordering wishlist item:', err);
      setError(err instanceof Error ? err.message : 'Failed to reorder wishlist item');
      
      // Revert the optimistic update
      await fetchLists();
      
      toast({
        title: 'Error',
        description: 'Failed to reorder wishlist item. Please try again.',
        variant: 'destructive',
      });
      
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const isPlayerInWishlist = (playerId: string): boolean => {
    if (!defaultWishlist) return false;
    return defaultWishlist.items.some(item => item.playerId === playerId);
  };

  useEffect(() => {
    if (user?.id) {
      fetchLists();
    }
  }, [user?.id]);

  return {
    lists,
    defaultWishlist,
    isLoading,
    error,
    fetchLists,
    getDefaultWishlist,
    addPlayerToWishlist,
    reorderWishlistItem,
    isPlayerInWishlist
  };
};